import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { ShieldAlert, RefreshCw, Loader2 } from 'lucide-react';

/**
 * ModerationFlagsCard — dev-only panel embedded in the scoring-debug view.
 *
 * Renders a scrollable list of the most recent moderation flags for the
 * signed-in parent's uploads (both hard `block` and borderline `flag`
 * verdicts). Wired to `POST /api/debug/moderation-flags`, which is PIN
 * gated by the same DEBUG_PIN as the parent debug panel — so no extra
 * auth flow is needed here. IMAGE CONTENT IS NEVER FETCHED OR SHOWN;
 * the backend returns fingerprint + scores + reason only.
 *
 * The tab filter is purely client-side over the last fetch — this keeps
 * the endpoint contract simple (a plain `limit`) and lets reviewers flip
 * between "blocked" and "flagged" without a round-trip.
 */
export default function ModerationFlagsCard({ debugPin }) {
  const [flags, setFlags] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('all'); // 'all' | 'block' | 'flag'
  const [limit, setLimit] = useState(50);

  const load = useCallback(async (nextLimit = limit) => {
    if (!debugPin) return;
    setBusy(true); setErr('');
    try {
      const { data } = await api.post(`/debug/moderation-flags?limit=${nextLimit}`, { pin: debugPin });
      setFlags(Array.isArray(data?.flags) ? data.flags : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load moderation flags');
    } finally { setBusy(false); }
  }, [debugPin, limit]);

  useEffect(() => { load(limit); /* on mount + limit change */ }, [load, limit]);

  const filtered = flags.filter((f) => {
    if (tab === 'block') return f.action === 'block';
    if (tab === 'flag') return f.action !== 'block' && f.borderline && !f.medical_exempt;
    if (tab === 'medical') return !!f.medical_exempt;
    return true;
  });

  const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      const hh = String(d.getHours()).padStart(2, '0');
      const mm = String(d.getMinutes()).padStart(2, '0');
      const mon = d.toLocaleString('en-US', { month: 'short' });
      return `${hh}:${mm} · ${mon} ${d.getDate()}`;
    } catch { return iso; }
  };

  const topScore = (f) => {
    // Highest of adult / racy / violence — this is the signal the parent
    // reviewer usually wants to eyeball. Medical is shown as a reason chip
    // instead so it doesn't dominate the number when it's really just an
    // exempt anatomy diagram.
    const scores = ['adult', 'racy', 'violence']
      .map((k) => [k, typeof f[k] === 'number' ? f[k] : null])
      .filter(([, v]) => v != null);
    if (!scores.length) return { label: '—', value: null };
    scores.sort((a, b) => b[1] - a[1]);
    const [label, value] = scores[0];
    return { label, value };
  };

  const tabBtn = (id, label, count) => (
    <button
      data-testid={`moderation-tab-${id}`}
      onClick={() => setTab(id)}
      className={`text-[12px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider ${
        tab === id ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
      }`}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  const counts = {
    all: flags.length,
    block: flags.filter((f) => f.action === 'block').length,
    flag: flags.filter((f) => f.action !== 'block' && f.borderline && !f.medical_exempt).length,
    medical: flags.filter((f) => !!f.medical_exempt).length,
  };

  return (
    <div data-testid="moderation-flags-card" className="mt-3 bg-white border border-slate-200 shadow-sm text-slate-800 rounded-2xl p-4 text-[13px]">
      <div className="flex items-center justify-between mb-3">
        <div className="text-slate-900 font-bold uppercase tracking-wider text-[13px] flex items-center gap-2">
          <ShieldAlert size={16}/> Moderation flags
        </div>
        <button data-testid="moderation-refresh-btn" onClick={() => load(limit)} disabled={busy}
          className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-slate-200 text-slate-800 hover:bg-slate-300 inline-flex items-center gap-1 disabled:opacity-60">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
          Refresh
        </button>
      </div>

      <div className="flex gap-1.5 flex-wrap mb-3">
        {tabBtn('all', 'All', counts.all)}
        {tabBtn('block', 'Blocked', counts.block)}
        {tabBtn('flag', 'Flagged', counts.flag)}
        {tabBtn('medical', 'Medical', counts.medical)}
        <div className="ml-auto">
          <button data-testid="moderation-toggle-limit"
            onClick={() => setLimit((l) => (l === 50 ? 100 : 50))}
            className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 uppercase tracking-wider">
            Show {limit === 50 ? '100' : '50'}
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="moderation-error" className="text-[13px] text-rose-600 mb-2">{err}</div>
      )}

      {!err && filtered.length === 0 && !busy && (
        <div data-testid="moderation-empty" className="text-[13px] text-slate-500 italic py-4 text-center">
          No moderation flags yet.
        </div>
      )}

      {filtered.length > 0 && (
        <div className="border border-slate-200 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
          <table className="w-full text-left text-[12px]">
            <thead className="bg-slate-100 text-slate-700 sticky top-0">
              <tr>
                <th className="px-2 py-1.5">time</th>
                <th className="px-2 py-1.5">decision</th>
                <th className="px-2 py-1.5">score</th>
                <th className="px-2 py-1.5">reason</th>
                <th className="px-2 py-1.5">fingerprint</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f, i) => {
                const isBlock = f.action === 'block';
                const isMedEx = !!f.medical_exempt;
                const decisionCls = isBlock
                  ? 'bg-rose-100 text-rose-800'
                  : isMedEx
                  ? 'bg-sky-100 text-sky-800'
                  : f.borderline
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-slate-100 text-slate-700';
                const decisionLabel = isBlock
                  ? 'block'
                  : isMedEx
                  ? 'medical'
                  : f.borderline
                  ? 'flag'
                  : (f.action || 'allow');
                const top = topScore(f);
                const reasonParts = [];
                if (f.reason) reasonParts.push(String(f.reason));
                if (f.is_medical) reasonParts.push('medical');
                if (f.is_book === false) reasonParts.push('non-book');
                return (
                  <tr key={f.flag_id || i} data-testid={`moderation-row-${i}`} className="border-t border-slate-200 align-top">
                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-700">{fmtTime(f.created_at)}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold uppercase ${decisionCls}`}>
                        {decisionLabel}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      {top.value != null ? (
                        <span className="text-slate-800">
                          {top.label}={top.value.toFixed ? top.value.toFixed(2) : top.value}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-slate-700">{reasonParts.join(' · ') || '—'}</td>
                    <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500 break-all">{f.image_fingerprint || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
        Fingerprint = first16…last16 chars of the uploaded image's base64 payload — enough to correlate with an upload but never the image itself. Medical anatomy diagrams are intentionally exempt (logged, not blocked).
      </p>
    </div>
  );
}
